import React from 'react';
import clsx from 'clsx';
import { makeStyles, Theme, Typography } from '@material-ui/core';
import BackgroundThumbnail from '../BackgroundThumbnail/BackgroundThumbnail';
import { backgroundConfig } from '../../VideoProvider/useBackgroundSettings/useBackgroundSettings';

/**
 * 'responsive' lays the pictures out as a wrapped grid on desktop and as a
 * horizontally scrolling strip on small screens (used on the setup screen).
 * 'grid' always wraps (used inside the in-call panel / bottom sheet).
 */
export type BackgroundOptionsLayout = 'responsive' | 'grid';

interface BackgroundOptionsProps {
	layout?: BackgroundOptionsLayout;
	className?: string;
}

const useStyles = makeStyles((theme: Theme) => ({
	root: {
		maxWidth: '100%',
		minWidth: 0,
	},
	effects: {
		display: 'grid',
		gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
		gap: 12,
		marginBottom: 16,
	},
	caption: {
		color: theme.palette.text.secondary,
		marginBottom: 8,
	},
	pictures: {
		display: 'grid',
		gap: 10,
		gridTemplateColumns: 'repeat(auto-fill, minmax(104px, 1fr))',
		maxWidth: '100%',
	},
	responsive: {
		[theme.breakpoints.down('sm')]: {
			display: 'flex',
			flexWrap: 'nowrap',
			overflowX: 'auto',
			padding: '4px 4px 8px',
			margin: '0 -4px',
			scrollSnapType: 'x proximity',
			WebkitOverflowScrolling: 'touch',
			'& > *': {
				flex: '0 0 116px',
				scrollSnapAlign: 'start',
			},
		},
	},
	grid: {
		gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
		[theme.breakpoints.down('sm')]: {
			gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
		},
	},
	// The side panel is narrow, so stack None / Blur on desktop.
	effectsStacked: {
		[theme.breakpoints.up('md')]: {
			gridTemplateColumns: 'minmax(0, 1fr)',
		},
	},
}));

export default function BackgroundOptions({
	layout = 'responsive',
	className,
}: BackgroundOptionsProps) {
	const classes = useStyles();
	const { images, imageNames } = backgroundConfig;

	return (
		<div
			role="group"
			aria-label="Background options"
			className={clsx(classes.root, className)}
		>
			<div
				className={clsx(classes.effects, {
					[classes.effectsStacked]: layout === 'grid',
				})}
			>
				<BackgroundThumbnail
					thumbnail="none"
					name="None"
					variant="card"
				/>
				<BackgroundThumbnail
					thumbnail="blur"
					name="Blur"
					variant="card"
				/>
			</div>
			<Typography
				variant="body2"
				component="p"
				className={classes.caption}
			>
				Or choose a picture
			</Typography>
			<div className={clsx(classes.pictures, classes[layout])}>
				{images.map((image, index) => (
					<BackgroundThumbnail
						key={image}
						thumbnail="image"
						name={imageNames[index]}
						index={index}
						imagePath={image}
					/>
				))}
			</div>
		</div>
	);
}
